import type {PickerVariable, TranslateFn, VariableDefinition} from "./types";

/** The definitions for `keys`, in catalogue order rather than the order asked for. */
export const selectVariables = <T extends VariableDefinition>(
    definitions: readonly T[],
    keys: readonly string[],
): T[] => {
    const wanted = new Set(keys);

    return definitions.filter(definition => wanted.has(definition.key));
};

/** A `TranslateFn` fits (it ignores the options), and so does an i18next `t` passed as it is. */
type CatalogueTranslate = (text: string, options: {nsSeparator: false}) => string;

const untranslated: TranslateFn = text => text;

/**
 * The catalogue in the author's language; the values stay in the guest's.
 *
 * Catalogue texts may contain a colon, which i18next would read as a namespace. So `t` is called
 * with `nsSeparator: false` here — a host can hand over `t` itself, or `text => t(text, {nsSeparator: false})`.
 */
export const variablesFor = (
    definitions: readonly VariableDefinition[],
    translate: CatalogueTranslate = untranslated,
): PickerVariable[] => {
    const text = (source: string) => translate(source, {nsSeparator: false});

    return definitions.map(variable => ({
        ...variable,
        label: text(variable.label),
        description: text(variable.description),
        scope: text(variable.scope),
    }));
};
